import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';

const LandingPWAContainer = styled.div`
  height: 100vh;
  width: 100%;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  padding: 20px;
  box-sizing: border-box;
  
  /* Desktop PWA constraint */
  @media (min-width: 768px) {
    max-width: 414px;
    width: 414px;
    margin: 0 auto;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.8), 
                0 0 0 12px rgba(0, 0, 0, 0.5),
                0 0 0 16px #333333;
    border-radius: 24px;
    left: 50%;
    transform: translateX(-50%);
    position: relative;
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  z-index: 1;
  flex: 1;
  max-width: 600px;
`;

const HeroTitle = styled.h1`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  font-size: 2.5rem;
  line-height: 1.1;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #e9d5ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 500px;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 40px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const CTAButton = styled.button`
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(88, 28, 135, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 480px) {
    width: auto;
    padding: 12px 24px;
  }
`;

const MetricsContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
  gap: 24px;
  z-index: 2;
`;

const MetricsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
  
  @media (max-width: 480px) {
    gap: 24px;
  }
`;

const MetricCard = styled.div`
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const MetricValue = styled.div<{ $color?: string }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.$color || '#bc13fe'};
  text-shadow: 0 0 20px ${props => {
    if (!props.$color) return 'rgba(188, 19, 254, 0.4)';
    if (props.$color === '#10b981') return 'rgba(16, 185, 129, 0.4)';
    if (props.$color === '#3b82f6') return 'rgba(59, 130, 246, 0.4)';
    return 'rgba(188, 19, 254, 0.4)';
  }};
  margin-bottom: 8px;
  line-height: 1;
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const MetricValueGreen = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #10b981;
  text-shadow: 0 0 20px rgba(16, 185, 129, 0.4);
  margin-bottom: 8px;
  line-height: 1;
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const BackgroundGradient = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at center, rgba(88, 28, 135, 0.15) 0%, rgba(0, 0, 0, 0) 70%);
  z-index: 0;
  pointer-events: none;
`;

const LandingPagePWA: React.FC = () => {
  const navigate = useNavigate();
  const [marketStats, setMarketStats] = useState<{ total: number; protofire: number; inkyswap: number } | null>(null);
  const [visitors, setVisitors] = useState<number | null>(null);
  const [impressions, setImpressions] = useState<number | null>(null);

  // Add desktop PWA mode class to body for styling
  useEffect(() => {
    if (window.innerWidth >= 768) {
      document.body.classList.add('pwa-desktop-mode');
    }

    const handleResize = () => {
      if (window.innerWidth >= 768 && window.location.pathname.startsWith('/pwa')) {
        document.body.classList.add('pwa-desktop-mode');
      } else {
        document.body.classList.remove('pwa-desktop-mode');
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      // Always remove class when component unmounts (leaving PWA routes)
      document.body.classList.remove('pwa-desktop-mode');
    };
  }, []);

  // Track visitor and fetch analytics data from API
  useEffect(() => {
    const trackAndFetch = async () => {
      try {
        // Call the API endpoint - it will track the visitor and return analytics data
        const response = await fetch('/api/analytics/visitors?page=/pwa');
        if (response.ok) {
          const data = await response.json();
          setVisitors(data.visitors || 0);
          setImpressions(data.pageViews || 0);
        } else {
          setVisitors(0);
          setImpressions(0);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
        setVisitors(0);
        setImpressions(0);
      }
    };

    trackAndFetch();
  }, []);

  // Fetch token count from Supabase
  useEffect(() => {
    const fetchTokenCount = async () => {
      try {
        // Get total count from tokens table (all Ink network tokens)
        const totalResult = await supabase
          .from('tokens')
          .select('id', { count: 'exact' })
          .eq('network', 'ink');

        // Get breakdown by DEX
        const [protofireResult, inkyswapResult] = await Promise.all([
          // Protofire: dex is null or dex = 'Protofire'
          supabase
            .from('tokens')
            .select('id', { count: 'exact' })
            .eq('network', 'ink')
            .or('dex.is.null,dex.eq.Protofire'),
          // InkySwap: dex = 'InkySwap'
          supabase
            .from('tokens')
            .select('id', { count: 'exact' })
            .eq('network', 'ink')
            .eq('dex', 'InkySwap')
        ]);

        if (!totalResult.error && !protofireResult.error && !inkyswapResult.error) {
          const total = totalResult.count || 0;
          const protofire = protofireResult.count || 0;
          const inkyswap = inkyswapResult.count || 0;

          console.log('PWA Token count:', { protofire, inkyswap, total });
          setMarketStats({
            total,
            protofire,
            inkyswap
          });
        } else {
          console.error('Error fetching token count:', totalResult.error || protofireResult.error || inkyswapResult.error);
        }
      } catch (error) {
        console.error('Error fetching token count:', error);
      }
    };

    fetchTokenCount();
  }, []);

  const handleCreateToken = () => {
    navigate('/pwa/create');
  };

  const handleLaunchApp = () => {
    navigate('/pwa/explore');
  };

  return (
    <LandingPWAContainer>
      <BackgroundGradient />
      <HeroContent>
        <HeroTitle>
          Capital-Free Market Formation
        </HeroTitle>
        <HeroSubtitle>
          Mavrk is the missing primitive for market formation. Liquidity as a computational process, not a capital problem.
        </HeroSubtitle>
        <CTAButtons>
          <CTAButton onClick={handleCreateToken}>
            Create Token
          </CTAButton>
          <CTAButton onClick={handleLaunchApp}>
            Launch App
          </CTAButton>
        </CTAButtons>
      </HeroContent>
      
      <MetricsContainer>
        <MetricsRow>
          {visitors !== null && (
            <MetricCard>
              <MetricValueGreen>{visitors.toLocaleString()}</MetricValueGreen>
              <MetricLabel>Visitors</MetricLabel>
            </MetricCard>
          )}
          {impressions !== null && (
            <MetricCard>
              <MetricValue $color="#3b82f6">{impressions.toLocaleString()}</MetricValue>
              <MetricLabel>Impressions</MetricLabel>
            </MetricCard>
          )}
        </MetricsRow>
        {marketStats?.total !== undefined && (
          <MetricCard>
            <MetricValue>{marketStats.total.toLocaleString()}</MetricValue>
            <MetricLabel>Markets Formed</MetricLabel>
          </MetricCard>
        )}
      </MetricsContainer>
    </LandingPWAContainer>
  );
};

export default LandingPagePWA;

